import type { LeagueData, StandingRow } from "../../schemas.js";
import { computeStandings, record, teamMetrics, weeksOf, type TeamMetrics } from "../../standings.js";
import { gamesForWeek } from "../data.js";
import { href } from "../router.js";
import { esc, fmt1, gameRow, moveText, segmented, teamLink } from "../ui.js";
import type { View } from "./view.js";

export type StandingsMode = "actual" | "optimal";
export type MetricKey = "rank" | "team" | "record" | "points" | "against" | "missed" | "efficiency";
type Section = "standings" | "matchups";
type Detail = "summary" | "analysis";
let mode: StandingsMode = "optimal";
let section: Section = "standings";
let detail: Detail = "summary";
let sort: MetricKey = "rank";
let ascending = true;

interface Metric {
  key: MetricKey;
  label: string;
  value(row: StandingRow, metrics: TeamMetrics): number | string;
  cell(row: StandingRow, metrics: TeamMetrics): string;
}
const metricColumns: Metric[] = [
  { key: "record", label: "Record", value: r => r.wins + r.ties / 2, cell: r => record(r) },
  { key: "points", label: "Points", value: r => r.pointsFor, cell: r => fmt1(r.pointsFor) },
  { key: "against", label: "Against", value: r => r.pointsAgainst, cell: r => fmt1(r.pointsAgainst) },
  { key: "missed", label: "Missed", value: (_r, m) => m.missed, cell: (_r, m) => fmt1(m.missed) },
  { key: "efficiency", label: "Eff. %", value: (_r, m) => m.efficiency, cell: (_r, m) => `${Math.round(m.efficiency * 100)}%` },
];

/** Sorting changes display order only; rank remains the actual standings rank. */
export function sortStandings(rows: StandingRow[], metrics: Map<string, TeamMetrics>, key: MetricKey, asc: boolean): StandingRow[] {
  const value = (r: StandingRow): string | number => {
    const m = metrics.get(r.team.teamId)!;
    if (key === "rank") return rows.indexOf(r);
    if (key === "team") return r.team.name;
    return metricColumns.find(c => c.key === key)!.value(r, m);
  };
  return [...rows].sort((a, b) => {
    const av = value(a), bv = value(b);
    const compared = typeof av === "string" && typeof bv === "string" ? av.localeCompare(bv) : Number(av) - Number(bv);
    return (asc ? compared : -compared) || rows.indexOf(a) - rows.indexOf(b);
  });
}

export function homeView(league: LeagueData, requestedWeek?: number): View {
  const standings = computeStandings(league);
  const metrics = teamMetrics(league);
  const weeks = weeksOf(league);
  const week = requestedWeek && weeks.includes(requestedWeek) ? requestedWeek : weeks.at(-1);
  section = requestedWeek && week ? "matchups" : "standings";

  const table = (): string => {
    const rows = standings[mode];
    const cols = metricColumns.filter(c => detail === "analysis" || ["record", "points"].includes(c.key));
    const heading = (key: MetricKey, label: string, cls = "") => `<th scope="col" class="${cls}" aria-sort="${sort === key ? ascending ? "ascending" : "descending" : "none"}"><button type="button" data-sort="${key}">${label}<span aria-hidden="true">${sort === key ? ascending ? " ↑" : " ↓" : ""}</span></button></th>`;
    return `<table class="stable"><caption class="visually-hidden">${mode === "actual" ? "Actual" : "Best-lineup"} standings</caption><thead><tr>
      ${heading("rank", "#", "c-rank")}${heading("team", "Team", "c-team")}${cols.map(c => heading(c.key, c.label)).join("")}
      </tr></thead><tbody>${sortStandings(rows, metrics, sort, ascending).map(r => {
        const m = metrics.get(r.team.teamId)!;
        return `<tr><td class="c-rank">${rows.indexOf(r) + 1}</td><th scope="row" class="c-team"><div class="team-identity">${teamLink(r.team, false)}<span class="rank-change">${mode === "optimal" ? moveText(m.move) : ""}</span></div></th>${cols.map(c => `<td class="metric-${c.key}">${c.cell(r, m)}</td>`).join("")}</tr>`;
      }).join("") || '<tr><td colspan="7">No teams yet.</td></tr>'}</tbody></table>`;
  };

  const html = `<div id="home" data-section="${section}">
    <div class="home-nav">${segmented("section", "League view", [["standings", "Standings"], ["matchups", "Matchups"]], section)}<span class="season-note">${weeks.length} ${weeks.length === 1 ? "week" : "weeks"} recorded</span></div>
    <section class="standings-section" aria-label="Standings">
      <div class="table-toolbar">${segmented("standings", "Scoring basis", [["actual", "Actual"], ["optimal", "Best lineup"]], mode)}
        <label class="picker">Columns <select id="columns-select"><option value="summary"${detail === "summary" ? " selected" : ""}>Summary</option><option value="analysis"${detail === "analysis" ? " selected" : ""}>Analysis</option></select></label>
      </div>
      <div id="standings-scroll" class="data-scroll" tabindex="0" role="region" aria-label="Standings table">${table()}</div>
    </section>
    <section class="matchups" aria-label="Matchups"><div class="table-toolbar"><h2>Matchups</h2>${week ? `<label class="picker">Week <select id="week-select">${weeks.map(w => `<option value="${w}"${w === week ? " selected" : ""}>${w}</option>`).join("")}</select></label>` : ""}</div><div id="matchups-list">${week ? matchupRows(league, week) : '<p class="hint">No games yet.</p>'}</div></section>
    </div>`;
  return {
    html,
    mount(root) {
      const home = root.querySelector<HTMLElement>("#home")!;
      const redraw = () => {
        root.querySelector("#standings-scroll")!.innerHTML = table();
      };
      home.addEventListener("click", e => {
        const button = (e.target as HTMLElement).closest<HTMLButtonElement>("button");
        if (!button) return;
        if (button.dataset.sort) {
          const key = button.dataset.sort as MetricKey;
          ascending = key === sort ? !ascending : ["rank", "team", "missed"].includes(key);
          sort = key;
          redraw();
          root.querySelector<HTMLButtonElement>(`button[data-sort="${key}"]`)?.focus({ preventScroll: true });
        }
        if (button.dataset.standings) {
          mode = button.dataset.standings as StandingsMode;
          root.querySelectorAll("button[data-standings]").forEach(b => b.setAttribute("aria-pressed", String((b as HTMLElement).dataset.standings === mode)));
          redraw();
        }
        if (button.dataset.section) {
          section = button.dataset.section as Section;
          home.dataset.section = section;
          root.querySelectorAll("button[data-section]").forEach(b => b.setAttribute("aria-pressed", String((b as HTMLElement).dataset.section === section)));
          syncUrl();
        }
      });
      const select = root.querySelector<HTMLSelectElement>("#week-select");
      const syncUrl = () => { const url = new URL(location.href); url.hash = section === "matchups" && select ? href.week(Number(select.value)) : href.home(); history.replaceState(null, "", url); };
      root.querySelector<HTMLSelectElement>("#columns-select")!.addEventListener("change", e => {
        detail = (e.target as HTMLSelectElement).value as Detail;
        if (detail === "summary" && !["rank", "team", "record", "points"].includes(sort)) { sort = "rank"; ascending = true; }
        redraw();
      });
      select?.addEventListener("change", () => { root.querySelector("#matchups-list")!.innerHTML = matchupRows(league, Number(select.value)); syncUrl(); });
    },
  };
}
function matchupRows(league: LeagueData, week: number): string {
  const games = gamesForWeek(league, week);
  return games.length ? games.map(g => gameRow(g)).join("") : `<p class="hint">No games for week ${week}.</p>`;
}
